(function (global) {
  'use strict';

  const MAX_CACHE_AGE_SECONDS = 7 * 24 * 60 * 60;
  const CONFIG_FILE = 'gb-seasonal-calendar.json';
  const evaluatedScript = global.document ? global.document.currentScript : null;

  function ageSeconds(headers) {
    if (!headers || typeof headers.get !== 'function') return 0;
    const raw = headers.get('age');
    if (raw == null || raw === '') return 0;
    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed) || parsed < 0 || parsed > MAX_CACHE_AGE_SECONDS) return null;
    return parsed;
  }

  function serverNowFromHeaders(headers, roundTripMs) {
    if (!headers || typeof headers.get !== 'function') return null;
    const dateMs = Date.parse(headers.get('date') || '');
    const cacheAge = ageSeconds(headers);
    if (!Number.isFinite(dateMs) || cacheAge === null) return null;
    const safeRoundTrip = Number.isFinite(roundTripMs) ? Math.max(0, roundTripMs) : 0;
    // Shopify's edge sends a current Date header while Age reports object cache
    // residence. Adding Age to that Date advances the visitor clock twice.
    return dateMs + safeRoundTrip / 2;
  }

  function selectCampaign(events, nowMs) {
    if (!Array.isArray(events) || !events.length || !Number.isFinite(nowMs)) return null;
    const ordered = [...events].sort((a, b) => a.start_ms - b.start_ms);
    const first = ordered[0];
    if (nowMs < first.start_ms && first.pre_horizon_fallback) return first;
    return ordered.find(event => nowMs >= event.start_ms && nowMs < event.end_ms) || null;
  }

  function validCalendar(config) {
    return !!(
      config &&
      config.schema_version === 2 &&
      Array.isArray(config.events) &&
      config.events.length > 0 &&
      config.events.every(event =>
        event && typeof event.id === 'string' &&
        Number.isFinite(event.start_ms) && Number.isFinite(event.end_ms) &&
        event.end_ms > event.start_ms &&
        typeof event.countdown_title === 'string' &&
        typeof event.end_label === 'string' &&
        (event.id === 'final_stock_sale' || (
          typeof event.desktop_url === 'string' &&
          typeof event.mobile_url === 'string' &&
          event.desktop_dimensions && Number.isFinite(event.desktop_dimensions.width) && Number.isFinite(event.desktop_dimensions.height) &&
          event.mobile_dimensions && Number.isFinite(event.mobile_dimensions.width) && Number.isFinite(event.mobile_dimensions.height) &&
          event.palette && typeof event.alt_text === 'string'
        ))
      )
    );
  }

  function configUrlFor(document, script) {
    const explicit = script && script.dataset && script.dataset.configUrl;
    if (explicit) return explicit;
    const secondary = document.querySelector('[data-gb-seasonal-config-url]');
    if (secondary && secondary.dataset && secondary.dataset.gbSeasonalConfigUrl) {
      return secondary.dataset.gbSeasonalConfigUrl;
    }
    if (script && script.src) {
      try { return new URL(CONFIG_FILE, script.src).href; } catch (ignore) {}
    }
    return '';
  }

  function nextLocalMidnightEpochMs(nowMs) {
    const nextMidnight = new Date(nowMs);
    nextMidnight.setHours(24, 0, 0, 0);
    return nextMidnight.getTime();
  }

  function countdownParts(endMs, nowMs) {
    const totalSeconds = Math.max(0, Math.floor((endMs - nowMs) / 1000));
    return {
      total_seconds: totalSeconds,
      hours: Math.floor(totalSeconds / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
  }

  function timerOverrideApplies(productOverride, eventId) {
    return productOverride === true && eventId === 'final_stock_sale';
  }

  function twoDigits(value) {
    return String(value).padStart(2, '0');
  }

  function eventFromServerMarkup(root) {
    if (!root) return null;
    const endMs = Number(root.dataset.gbEndEpochMs);
    if (!Number.isFinite(endMs)) return null;
    return {
      id: root.dataset.gbEventId || '',
      end_ms: endMs,
      countdown_title: root.dataset.gbTitle || '',
      end_label: root.dataset.gbEndLabel || '',
      palette: null,
      desktop_url: null,
      mobile_url: null,
    };
  }

  function setText(root, selector, value) {
    const element = root && root.querySelector(selector);
    if (element && element.textContent !== value) element.textContent = value;
  }

  function cssSet(element, name, value) {
    if (!element || !element.style) return;
    if (value) element.style.setProperty(name, value);
    else element.style.removeProperty(name);
  }

  function applyPalette(root, palette) {
    if (!root) return;
    const values = palette || {};
    cssSet(root, '--gb-cx-background', values.background);
    cssSet(root, '--gb-cx-accent', values.accent);
    cssSet(root, '--gb-cx-title', values.title);
    cssSet(root, '--gb-cx-text', values.text);
    cssSet(root, '--gb-cx-box-background', values.box_background);
    cssSet(root, '--gb-cx-box-text', values.box_text);
    cssSet(root, '--gb-cx-box-label', values.box_label);
  }

  function applyPromoImage(document, event) {
    const promo = document.querySelector('[data-gb-seasonal-promo]');
    if (!promo) return;
    const picture = promo.querySelector('[data-gb-seasonal-picture]');
    if (!event) {
      promo.hidden = true;
      promo.setAttribute('aria-hidden', 'true');
      return;
    }
    promo.hidden = false;
    promo.removeAttribute('aria-hidden');
    if (!picture || event.id === 'final_stock_sale') return;

    const source = picture.querySelector('[data-gb-seasonal-mobile-source]');
    const image = picture.querySelector('[data-gb-seasonal-image]');
    if (source && event.mobile_url && source.getAttribute('srcset') !== event.mobile_url) {
      source.setAttribute('srcset', event.mobile_url);
    }
    if (image && event.desktop_url && image.getAttribute('src') !== event.desktop_url) {
      image.setAttribute('src', event.desktop_url);
    }
    if (image && event.alt_text && image.getAttribute('alt') !== event.alt_text) {
      image.setAttribute('alt', event.alt_text);
    }
    if (image && event.desktop_dimensions) {
      image.setAttribute('width', String(event.desktop_dimensions.width));
      image.setAttribute('height', String(event.desktop_dimensions.height));
    }
    picture.dataset.gbEventId = event.id;
  }

  function saveOriginalStyle(element, property, key) {
    if (!element || element.dataset[key] === 'captured') return;
    element.dataset[key] = 'captured';
    element.dataset[key + 'Value'] = element.style[property] || '';
  }

  function restoreStyle(element, property, key) {
    if (!element || element.dataset[key] !== 'captured') return;
    element.style[property] = element.dataset[key + 'Value'] || '';
    delete element.dataset[key];
    delete element.dataset[key + 'Value'];
  }

  function restoreOffset(document, root, host) {
    const body = document.body;
    if (root) restoreStyle(body, 'marginTop', 'gbSeasonalOriginalMarginTop');
    if (host) {
      restoreStyle(host, 'margin', 'gbSeasonalOriginalMargin');
      restoreStyle(host, 'padding', 'gbSeasonalOriginalPadding');
      restoreStyle(host, 'lineHeight', 'gbSeasonalOriginalLineHeight');
      restoreStyle(host, 'display', 'gbSeasonalOriginalDisplay');
      host.hidden = true;
      host.style.display = 'none';
    }
    document.documentElement.style.removeProperty('--cx-banner-height');
    if (body.dataset.cxOffset === 'true') delete body.dataset.cxOffset;
  }

  function syncOffset(document, root, host) {
    if (!root || root.hidden || global.getComputedStyle(root).position !== 'fixed') return;
    const body = document.body;
    saveOriginalStyle(body, 'marginTop', 'gbSeasonalOriginalMarginTop');
    if (host) {
      saveOriginalStyle(host, 'margin', 'gbSeasonalOriginalMargin');
      saveOriginalStyle(host, 'padding', 'gbSeasonalOriginalPadding');
      saveOriginalStyle(host, 'lineHeight', 'gbSeasonalOriginalLineHeight');
      saveOriginalStyle(host, 'display', 'gbSeasonalOriginalDisplay');
      host.hidden = false;
      restoreStyle(host, 'display', 'gbSeasonalOriginalDisplay');
      host.style.margin = '0';
      host.style.padding = '0';
      host.style.lineHeight = '0';
    }
    const height = Math.ceil(root.getBoundingClientRect().height);
    if (height > 0) {
      document.documentElement.style.setProperty('--cx-banner-height', height + 'px');
      body.style.marginTop = height + 'px';
      body.dataset.cxOffset = 'true';
    }
  }

  function renderCountdown(root, event, nowMs, document, host) {
    if (!root) return;
    root.hidden = false;
    root.removeAttribute('aria-hidden');
    root.dataset.gbEventId = event.id;
    root.dataset.gbTitle = event.countdown_title || '';
    root.dataset.gbEndEpochMs = String(event.end_ms);
    root.dataset.gbEndLabel = event.end_label || '';
    setText(root, '[data-gb-seasonal-copy]', event.countdown_title || '');
    applyPalette(root, event.palette);

    const productOverride = root.dataset.gbProductTimerOverride === 'true';
    const timerOverride = timerOverrideApplies(productOverride, event.id);
    root.dataset.gbTimerOverride = timerOverride ? 'true' : 'false';

    const timer = root.querySelector('[data-gb-seasonal-clock]');
    if (timer) {
      const timerDescription = timerOverride
        ? 'Ends ' + (event.end_label || 'at the scheduled end time')
        : 'Ends at midnight in your local time';
      timer.setAttribute('aria-label', (event.countdown_title || 'Sale') + '. ' + timerDescription + '.');
    }

    if (!timerOverride) {
      const parts = countdownParts(nextLocalMidnightEpochMs(nowMs), nowMs);
      setText(root, '[data-h]', twoDigits(parts.hours));
      setText(root, '[data-m]', twoDigits(parts.minutes));
      setText(root, '[data-s]', twoDigits(parts.seconds));
    }
    root.dataset.gbSeasonalReady = 'true';
    syncOffset(document, root, host);
  }

  function hideSeasonalSurface(document, root, host) {
    if (root) {
      root.dataset.gbEventId = 'none';
      root.dataset.gbTimerOverride = 'false';
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
    }
    applyPromoImage(document, null);
    restoreOffset(document, root, host);
  }

  async function fetchCalendarAndClock(fetchImpl, configUrl, performanceImpl) {
    const start = performanceImpl.now();
    const response = await fetchImpl(configUrl, { cache: 'no-store', credentials: 'omit' });
    const end = performanceImpl.now();
    if (!response || !response.ok) throw new Error('calendar config fetch failed');
    const config = await response.json();
    if (!validCalendar(config)) throw new Error('calendar config shape is invalid');
    return {
      config,
      server_ms: serverNowFromHeaders(response.headers, end - start),
      sampled_at_performance_ms: end,
    };
  }

  async function boot(document, windowObject) {
    if (windowObject.__gbSeasonalCalendarBooted) return;
    windowObject.__gbSeasonalCalendarBooted = true;

    const script = evaluatedScript ||
      document.querySelector('script[data-gb-seasonal-calendar-loader]') ||
      document.getElementById('gb-seasonal-calendar-loader');
    const configUrl = configUrlFor(document, script);
    const host = (script && script.closest('.shopify-section')) || null;
    if (!configUrl) {
      if (global.console && global.console.error) {
        global.console.error('[GetBlaster seasonal calendar] Config URL is missing; keeping the server-rendered campaign.');
      }
      return;
    }
    const loadPerf = windowObject.performance.now();
    const initialRoot = document.getElementById('countdown-banner');
    const fallbackMs = initialRoot ? Number(initialRoot.dataset.gbServerNowMs) : NaN;
    const currentRoot = () => document.getElementById('countdown-banner');
    let calendar = null;
    let clock = null;
    let refreshInFlight = null;
    let activeEvent = eventFromServerMarkup(initialRoot);
    let observedRoot = null;
    let resizeObserver = null;

    function nowMs() {
      if (clock) return clock.server_ms + Math.max(0, windowObject.performance.now() - clock.sampled_at_performance_ms);
      if (Number.isFinite(fallbackMs)) return fallbackMs + Math.max(0, windowObject.performance.now() - loadPerf);
      return Date.now();
    }

    function draw() {
      const root = currentRoot();
      if (!calendar) {
        if (activeEvent && root) renderCountdown(root, activeEvent, nowMs(), document, host);
        return;
      }
      activeEvent = selectCampaign(calendar.events, nowMs());
      if (!activeEvent) {
        hideSeasonalSurface(document, root, host);
        return;
      }
      if (host) host.hidden = false;
      applyPromoImage(document, activeEvent);
      renderCountdown(root, activeEvent, nowMs(), document, host);
      if (windowObject.ResizeObserver && root && root !== observedRoot) {
        if (resizeObserver) resizeObserver.disconnect();
        resizeObserver = new windowObject.ResizeObserver(() => syncOffset(document, currentRoot(), host));
        resizeObserver.observe(root);
        observedRoot = root;
      }
    }

    async function refresh() {
      if (refreshInFlight || !configUrl) return refreshInFlight;
      refreshInFlight = fetchCalendarAndClock(windowObject.fetch.bind(windowObject), configUrl, windowObject.performance)
        .then(result => {
          calendar = result.config;
          if (Number.isFinite(result.server_ms)) clock = result;
          draw();
        })
        .catch(error => {
          if (global.console && global.console.error) {
            global.console.error('[GetBlaster seasonal calendar] Config fetch failed; using the server-rendered fallback.', error);
          }
          draw();
        })
        .finally(() => { refreshInFlight = null; });
      return refreshInFlight;
    }

    draw();
    await refresh();

    if (initialRoot) windowObject.setInterval(draw, 1000);
    let resizeTimer = 0;
    windowObject.addEventListener('resize', () => {
      windowObject.clearTimeout(resizeTimer);
      resizeTimer = windowObject.setTimeout(() => syncOffset(document, currentRoot(), host), 50);
    }, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refresh();
    });
    windowObject.addEventListener('pageshow', () => refresh());
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => syncOffset(document, currentRoot(), host));
  }

  const helpers = {
    ageSeconds,
    serverNowFromHeaders,
    selectCampaign,
    countdownParts,
    nextLocalMidnightEpochMs,
    timerOverrideApplies,
    validCalendar,
    configUrlFor,
    twoDigits,
    fetchCalendarAndClock,
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;
  if (global.document) {
    const run = () => boot(global.document, global);
    if (global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded', run, { once: true });
    else run();
  }
})(typeof window !== 'undefined' ? window : globalThis);
